import { type ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import { SpaceBetween, tk, zIndex } from '@sunwebgroup/ui-components';

import { NewSunwebLogo, SunwebLogo } from './Logo';
import { HeaderLink } from './Link';
import { Navbar } from './Navbar';

import { useIntl } from '@/intl';
import { LiveSearch } from '@/scopes/search/search/pages/SearchPage/components/LiveSearch';
import * as React from 'react';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';
import { AccountMenu } from '@/scopes/common/components/Header/AccountMenu';

// Extend the props to include hideLiveSearch
interface HeaderProps extends ComponentPropsWithoutRef<'header'> {
  hideLiveSearch?: boolean;
  children?: React.ReactNode;
}

export function Header({ children, hideLiveSearch = false, ...props }: HeaderProps) {
  const intl = useIntl();

  const { isDesktopMd } = useBreakpoints();

  return (
    <Header.Root {...props}>
      <Header.Content>
        <Navbar>
          <Navbar.List>
            <Navbar.Item>
              <HeaderLink>
                {isDesktopMd && <SunwebLogo />}
                {!isDesktopMd && <NewSunwebLogo />}
                <ScreenReaderOnlyText>{intl.formatMessage({ id: 'link_home' })}</ScreenReaderOnlyText>
              </HeaderLink>
            </Navbar.Item>
            <Navbar.Item>{children}</Navbar.Item>
          </Navbar.List>
        </Navbar>
        <SpaceBetween direction="x">
          {!hideLiveSearch && <LiveSearch />}
          <AccountMenu />
        </SpaceBetween>
      </Header.Content>
    </Header.Root>
  );
}

Header.Content = styled.div`
  align-items: center;
  display: flex;
  margin: 0 auto;
  max-width: ${tk('breakpoint_desktop_xl')};
`;

Header.Root = styled.header`
  background: ${tk('color_semantic_bg')};
  padding: ${tk('spacing_xs')} ${tk('spacing_lg')};
  position: relative;
  z-index: ${zIndex.header};

  ${Header.Content} > ${Navbar} {
    margin-right: auto;

    ${Navbar.Item} {
      &:nth-of-type(2) {
        margin-left: ${tk('spacing_xs')};
      }
      &:not(:first-child) > *:first-child {
        padding: ${tk('spacing_xs')} ${tk('spacing_md')};
      }
    }
  }
`;

const ScreenReaderOnlyText = styled.span`
  clip: rect(0 0 0 0);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;
